/**
 * 图库时间展示与按日分组（纯函数）。
 *
 * 图库记录本身自 T11b 起就带 `createdAt`，这里只负责把它转成「用户看到的日期 /
 * 时间」以及把倒序图片流切成「一天一组」，供图库页按日期换行展示：
 * - 分组边界按**本地时区**的自然日（00:00–24:00）计算，跨时区换日与用户直觉一致；
 * - 组内保持 `createdAt` 倒序、同刻用 id 兜底（与 `galleryRepo` 的排序约定一致）；
 * - 所有函数都不读系统当前时间，只依赖入参时间戳，便于单测与未来安卓移植复用。
 */
import type { GalleryImage } from './galleryRepo';

/** 星期中文名，`Date.getDay()` 的 0 为周日。 */
const WEEKDAY_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const;

/** 无法解析的时间戳统一落到这一组，避免 NaN 把排序/分组弄乱。 */
const UNKNOWN_DAY_KEY = 'unknown';

export interface GalleryDayGroup {
  /** 本地日期键：`YYYY-MM-DD`（无法解析时为 `unknown`）。 */
  key: string;
  /** 该日 00:00 的本地时间戳（无法解析时为 0）。 */
  start: number;
  /** 分组标题：`2026年10月19日 周一`。 */
  label: string;
  /** 该日的图片，保持倒序。 */
  items: GalleryImage[];
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function validDate(timestamp: number): Date | null {
  if (!Number.isFinite(timestamp)) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `YYYY-MM-DD`（本地时区）。 */
export function formatGalleryDate(timestamp: number): string {
  const date = validDate(timestamp);
  if (date === null) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** `HH:mm`（本地时区，24 小时制）。 */
export function formatGalleryTime(timestamp: number): string {
  const date = validDate(timestamp);
  if (date === null) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** `YYYY-MM-DD HH:mm`（大图预览与无障碍文案用）。 */
export function formatGalleryDateTime(timestamp: number): string {
  const dateLabel = formatGalleryDate(timestamp);
  if (dateLabel === '') return '';
  return `${dateLabel} ${formatGalleryTime(timestamp)}`;
}

/** 缩略图下方的时间条：`MM-DD HH:mm`（年份交给日期分组标题，避免小格子拥挤）。 */
export function formatGalleryStamp(timestamp: number): string {
  const date = validDate(timestamp);
  if (date === null) return '';
  return `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${formatGalleryTime(timestamp)}`;
}

/** 日期分组标题：`2026年10月19日 周一`。 */
export function formatGalleryDayLabel(timestamp: number): string {
  const date = validDate(timestamp);
  if (date === null) return '未知日期';
  const weekday = WEEKDAY_LABELS[date.getDay()] ?? '';
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${weekday}`;
}

/** 本地自然日键 `YYYY-MM-DD`，用于分组。 */
export function galleryDayKey(timestamp: number): string {
  const dateLabel = formatGalleryDate(timestamp);
  return dateLabel === '' ? UNKNOWN_DAY_KEY : dateLabel;
}

/** 本地自然日 00:00 的时间戳。 */
export function startOfGalleryDay(timestamp: number): number {
  const date = validDate(timestamp);
  if (date === null) return 0;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** 与 `galleryRepo.compareGalleryImages` 相同的稳定倒序：createdAt 降序，同刻 id 升序。 */
function compareForGallery(a: GalleryImage, b: GalleryImage): number {
  const at = Number.isFinite(a.createdAt) ? a.createdAt : 0;
  const bt = Number.isFinite(b.createdAt) ? b.createdAt : 0;
  if (at !== bt) return bt - at;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function createGroup(image: GalleryImage): GalleryDayGroup {
  return {
    key: galleryDayKey(image.createdAt),
    start: startOfGalleryDay(image.createdAt),
    label: formatGalleryDayLabel(image.createdAt),
    items: [image],
  };
}

/**
 * 把图片流切成「一天一组」。
 *
 * 先按 `createdAt` 倒序排序（不依赖调用方顺序），再按本地自然日聚组；
 * 因此即使用户在图库页删除 / 收藏后局部更新了顺序，分组结果也保持稳定。
 */
export function groupGalleryImagesByDay(
  images: readonly GalleryImage[],
): GalleryDayGroup[] {
  const ordered = [...images].sort(compareForGallery);
  const groups: GalleryDayGroup[] = [];
  let current: GalleryDayGroup | null = null;

  for (const image of ordered) {
    const key = galleryDayKey(image.createdAt);
    if (current !== null && current.key === key) {
      current.items.push(image);
      continue;
    }
    current = createGroup(image);
    groups.push(current);
  }

  return groups;
}
